/**
 * Auth Context — who is signed in, and what the SERVER says they may do.
 *
 * Reads the JWT from localStorage, decodes it, and asks `GET /users/me` once.
 * That response carries the caller's resolved authority — `roleKeys[]` and
 * `permissions[]`, straight off `resolvePrincipal` — and this context hands it
 * to the shell.
 *
 * ─── What this is not, any more (Foundations 1 / B1-T3) ───────────────────
 *
 * It used to DERIVE authority. A `ROLE_LEVELS` ladder
 * (`viewer 0 … sysadmin 5`) sat here, `hasRole(minRole)` compared against it,
 * and three flags — `canWrite`, `canManageSources`, `canAdmin` — were computed
 * from it. `useHasPermission` separately indexed `ROLE_PERMISSIONS`, a copy of
 * the server's grant matrix compiled into the bundle.
 *
 * Both were wrong in the same way: a tenant's OWN role granted nothing (the
 * ladder had no rung for it, the matrix had no key for it), and a shipped
 * bundle that changed in a release stayed stale in every browser until the
 * bundle was rebuilt. So the shell offered buttons the gateway refuses and hid
 * buttons it allows.
 *
 * The rule now: the browser holds NO permission logic. `can(key)` is a lookup
 * in the set the server sent. An empty set is the honest answer for an
 * unauthenticated shell and for a bootstrap that could not read the response —
 * offering too little is one reload, offering too much is a screen of buttons
 * that all 403 (principles.md #4).
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { RbacPermission } from '@argusai/shared/schemas/permissions';
import {
  readMeAuthority,
  type MeAuthority,
} from '@argusai/shared/schemas/principal';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /**
   * The role key to SHOW ("Admin" in the profile card). Never an input to a
   * permission decision — `permissions` is (B1-T3).
   */
  role: string;
  /**
   * Every role key the server resolved for this person in this organization —
   * shipped and tenant-defined alike. Display and diagnostics only.
   */
  roleKeys: string[];
  /**
   * The union of those roles' grants, as `GET /users/me` returned it. This is
   * the ONLY thing the shell gates on.
   *
   * `string[]`, not `RbacPermission[]` (review B1T3-R3): it is the WIRE value,
   * and a browser running an older bundle must be able to hold a key its copy
   * of the vocabulary does not list and answer "no" for it — not discard the
   * whole set. `can()` below narrows the QUESTION to `RbacPermission`, which
   * is where the typed vocabulary belongs.
   */
  permissions: string[];
  organizationId: string | null;
  isAuthenticated: boolean;
}

/**
 * The active auth context — populated from the JWT audience claim.
 * 'tenant' = regular workspace (`argusai` audience)
 * 'sysadmin' = platform-level (`argusai-admin` audience, requires fresh TOTP)
 */
export type AuthContextKind = 'tenant' | 'sysadmin';

export interface AuthContextType {
  user: AuthUser;
  isLoading: boolean;
  /** Which JWT audience the active token represents. Drives sidebar nav. */
  contextKind: AuthContextKind;
  /** Shorthand: contextKind === 'sysadmin'. */
  isSysadmin: boolean;
  /**
   * Does the signed-in user hold `<action>:<resource>`?
   *
   * A lookup in the grant set `GET /users/me` returned — no ladder, no matrix,
   * no inference. The gateway answers the same question from the same
   * principal on every request, so an affordance this hides is one the API
   * would refuse, and an affordance it shows is one the API allows.
   *
   * Use it for affordances, never as the security barrier: the API enforces.
   */
  can: (permission: RbacPermission) => boolean;
  /** Refresh user info from API */
  refresh: () => Promise<void>;
  /**
   * Sign out the current user.
   *
   * Issue #87: clicking "Sign Out" used to just remove the localStorage
   * tokens and navigate to /login. That left:
   *   - the refresh token live on the server (POST /auth/refresh would
   *     still mint new access tokens for the same session)
   *   - the AuthProvider's React state populated with the previous user
   *     (so a back-button render still showed protected UI)
   *   - the TanStack Query cache full of tenant-scoped data
   *
   * This method does the full cleanup:
   *   1. POST /auth/logout with the refresh token (best-effort —
   *      server-side revocation + audit log)
   *   2. Clear localStorage (accessToken, refreshToken, authContext)
   *   3. Reset React state (user → DEFAULT_USER, contextKind → 'tenant')
   *   4. Clear the TanStack Query cache (every tenant-scoped query gone)
   *   5. Navigate to /login
   */
  signOut: () => Promise<void>;
}

// ─── Default (not authenticated) ─────────────────────────────────────────────

const DEFAULT_USER: AuthUser = {
  id: '',
  email: '',
  firstName: null,
  lastName: null,
  // The LEAST privilege, not the most (review BF-01, B1-T2b). This used to
  // read `'admin', // Default to admin during development so all features are
  // visible` — a development convenience that stayed. It is the state the
  // shell holds before the bootstrap answers, when there is no token, and
  // when a token fails to decode, and in every one of those we know nothing
  // about the user, which is the definition of "deny" (principles.md #4).
  role: 'viewer',
  // No token, no answer, no authority. An empty grant set is what `can()`
  // answers for an unresolvable principal on the server too, so the two agree
  // even here.
  roleKeys: [],
  permissions: [],
  organizationId: null,
  isAuthenticated: false,
};

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hard timeout for the auth bootstrap network calls. If the backend is
// slow / refreshing-token-handshake stalls / a fetch is stuck in retry,
// we MUST still flip `isLoading=false` so the sysadmin-vs-tenant gate
// can render. Without this, /sysadmin/runtime/*, /sysadmin/catalog/*,
// and /sysadmin/platform/* sit on "Loading…" forever (issues #960,
// #981, #932). 8s is generous for a healthy LAN, short enough that
// operators reload rather than wait.
const AUTH_FETCH_TIMEOUT_MS = 8000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error(`auth-bootstrap-timeout-${ms}ms`)),
      ms,
    );
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

// ─── The tenant profile, read from GET /users/me ─────────────────────────────

/**
 * What the bootstrap needs out of a `/users/me` response.
 *
 * The two AUTHORITY fields are not re-declared here: they are
 * `MeAuthoritySchema` (packages/shared/src/schemas/principal.ts), the same Zod
 * object the route types its response with (review B1T3-R3). Renaming
 * `roleKeys` on the server is now a compile error here instead of an empty
 * array in every browser.
 */
export interface TenantProfile extends MeAuthority {
  role: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/**
 * Read `GET /users/me` into the shell's idea of who is signed in.
 *
 * FAILS CLOSED (principles.md #4, review BF-01, B1-T2b). A response with no
 * role — an error, a timeout, a shape change, an empty object — yields
 * `viewer`, the least privilege, NOT `admin`. The predecessor of this function
 * was `let role = 'admin'` plus a `catch { role = payload.org?.role ?? 'admin'
 * }` over a JWT claim that has never carried a role, so every non-admin login
 * rendered the admin shell until a second request corrected it.
 *
 * This decides what the shell OFFERS, never what the user may do: the gateway
 * decides that, per request, from the grants. Offering too much is a screen of
 * buttons that all 403; offering too little is one reload.
 */
export function tenantProfileFrom(res: unknown): TenantProfile {
  const body = (res ?? {}) as Record<string, any>;
  const profile = (body.user ?? body) as Record<string, any>;
  return {
    role: typeof profile?.role === 'string' && profile.role ? profile.role : 'viewer',
    // The authority fields sit on the RESPONSE, not on the nested `user`
    // object, because they describe the caller's authority rather than their
    // record. `readMeAuthority` is the shared fail-closed reader: it walks
    // `MeAuthoritySchema.shape`, so it follows a rename of the wire shape
    // instead of drifting from it, and a missing or malformed field yields
    // `[]` — "holds nothing", never "unknown, so allow" (principles.md #4).
    ...readMeAuthority(body),
    firstName: profile?.firstName ?? null,
    lastName: profile?.lastName ?? null,
    email: profile?.email ?? null,
  };
}

// ─── signOut helpers (issue #87) ─────────────────────────────────────────────

/**
 * Dependencies the sign-out flow needs. Extracted so unit tests can
 * inject mocks without spinning up a React renderer — the workspace
 * deliberately ships without @testing-library/react.
 */
export interface SignOutDeps {
  /** Calls POST /auth/logout with the refresh token. Rejects on net/4xx/5xx. */
  postLogout: (refreshToken: string) => Promise<void>;
  /** Reads a key from token storage (real impl: localStorage.getItem). */
  readToken: (key: 'accessToken' | 'refreshToken' | 'authContext') => string | null;
  /** Removes all auth-related token storage entries. */
  clearTokens: () => void;
  /** Drops the TanStack Query cache (real impl: queryClient.clear()). */
  clearQueryCache: () => void;
  /** Resets the AuthProvider React state to the unauthenticated default. */
  resetAuthState: () => void;
  /** Navigates to the login page; real impl uses TanStack Router with replace:true. */
  navigateToLogin: () => void;
}

/**
 * Pure orchestrator for the sign-out flow. Issue #87 — the broken
 * implementation only cleared localStorage; this helper composes the
 * full teardown:
 *
 *   1. POST /auth/logout to revoke the refresh token server-side
 *      (best-effort — a failure here MUST NOT block the local teardown)
 *   2. Clear all auth-related token storage
 *   3. Reset AuthProvider React state to DEFAULT_USER
 *   4. Drop the TanStack Query cache
 *   5. Navigate to /login (with replace:true so back-button cannot
 *      restore the protected shell)
 *
 * Returns nothing — callers should `void` the promise.
 */
export async function performSignOut(deps: SignOutDeps): Promise<void> {
  const refreshToken = deps.readToken('refreshToken');

  if (refreshToken) {
    try {
      await deps.postLogout(refreshToken);
    } catch {
      // Server-side revocation failed — proceed with local teardown so
      // the user is not stuck in a stale session. The api-client tracker
      // captures the failed call for triage.
    }
  }

  deps.clearTokens();
  deps.resetAuthState();
  deps.clearQueryCache();
  deps.navigateToLogin();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser>(DEFAULT_USER);
  const [isLoading, setIsLoading] = useState(true);
  const [contextKind, setContextKind] = useState<AuthContextKind>('tenant');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setUser(DEFAULT_USER);
      setContextKind('tenant');
      setIsLoading(false);
      return;
    }

    try {
      // Decode JWT payload (without verification — server validates)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isSysadminToken = payload.aud === 'argusai-admin';
      setContextKind(isSysadminToken ? 'sysadmin' : 'tenant');

      // ── Sysadmin path: tenant endpoints would 401 with INVALID_AUDIENCE.
      //    Hit /admin/platform/_self instead — returns the same id+email shape.
      if (isSysadminToken) {
        try {
          const self = await withTimeout(
            apiClient.get<{ id: string; email: string; audience: string }>(
              '/admin/platform/_self',
            ),
            AUTH_FETCH_TIMEOUT_MS,
          );
          setUser({
            id: self.id,
            email: self.email,
            firstName: null,
            lastName: null,
            // A DISPLAY label for the platform audience, derived from the
            // token's own `aud` claim — not a resolved role.
            role: 'sysadmin',
            // EMPTY, and deliberately (review BF-8). `/admin/platform/_self`
            // returns `{id, email, audience}`: the server resolved no roles
            // here, so the shell states none. Writing `['sysadmin']` would be
            // the browser inventing an authority label in the file whose whole
            // contract is that it holds no permission logic — the same defect
            // in a smaller shape. The PLATFORM audience holds no TENANT grants
            // either — that is the orthogonality invariant, not an omission.
            // The sysadmin shell is driven by `isSysadmin` (the `aud` claim),
            // and the platform routes enforce their own slate server-side
            // (`sysadminCan`).
            roleKeys: [],
            permissions: [],
            organizationId: null,
            isAuthenticated: true,
          });
        } catch {
          // Token decoded but /admin/platform/_self failed or timed out —
          // fall back to JWT claims so the sysadmin shell can still
          // render (issues #960, #981).
          setUser({
            id: payload.sub ?? '',
            email: payload.email ?? '',
            firstName: null,
            lastName: null,
            role: 'sysadmin',
            // See above (review BF-8): the server resolved nothing, so the
            // shell claims nothing.
            roleKeys: [],
            permissions: [],
            organizationId: null,
            isAuthenticated: true,
          });
        }
        setIsLoading(false);
        return;
      }

      // ── Tenant path.
      //
      // ONE CALL, AND IT IS THE UNGATED ONE (review BF-01, B1-T2b). This used
      // to probe `GET /users/stats` first and treat a 403 as "not an admin",
      // falling back to `payload.org?.role ?? 'admin'`. Two things were wrong
      // with that and they compounded:
      //
      //   * The JWT's `org` claim carries no `role` at all — `auth.ts` builds
      //     it from rootOrganizationId / currentOrganizationId /
      //     accessibleOrganizationIds — so the fallback was always the literal
      //     'admin'. The probe failing therefore made the client MORE
      //     privileged, not less: a Viewer rendered the admin shell.
      //   * `/users/stats` is an admin route again (SEC-B1-F3), so the probe
      //     403s for every Viewer, Member and Power User — one guaranteed
      //     failed request per non-admin login, and a role that was only
      //     corrected if the background `/users/me` below happened to land
      //     inside the timeout.
      //
      // `GET /users/me` is ungated (routes/v1/users.ts — no preHandler; any
      // authenticated tenant member reads their own record) and it returns the
      // role. So ask it, once. Nothing about what the user may DO is decided
      // here anyway — the gateway decides, on every request; this only picks
      // what the shell offers. When we cannot learn the role, we offer the
      // LEAST (principles.md #4, nothing fails open): 'viewer' shows a shell
      // with no admin actions in it, which is recoverable by a reload, where
      // a wrongly-offered admin shell is a screen of buttons that all 403.
      // The SAME response carries the name and email the second, background
      // `/users/me` call used to fetch, so that call is gone rather than left
      // beside this one (principles.md #2 — one reader path, the old one
      // deleted). The bootstrap now makes exactly one tenant request.
      let profile = tenantProfileFrom(null);
      try {
        profile = tenantProfileFrom(
          await withTimeout(apiClient.get<any>('/users/me'), AUTH_FETCH_TIMEOUT_MS),
        );
      } catch {
        // Timed out or errored: the shell still renders (issue #932 — never
        // sit on "Loading…"), at the least privilege `tenantProfileFrom`
        // returns for an absent response.
      }

      setUser({
        id: payload.sub ?? '',
        email: profile.email ?? payload.email ?? '',
        firstName: profile.firstName,
        lastName: profile.lastName,
        role: profile.role,
        roleKeys: profile.roleKeys,
        permissions: profile.permissions,
        organizationId: payload.org?.currentOrganizationId ?? null,
        isAuthenticated: true,
      });

      setIsLoading(false);
      return;
    } catch {
      setUser(DEFAULT_USER);
      setContextKind('tenant');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Listen for login/logout (localStorage changes)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'accessToken') fetchUser();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [fetchUser]);

  // The grant set as a Set, rebuilt only when the array itself changes, so
  // `can` is O(1) from a render path called on every nav item.
  const grants = useMemo(
    () => new Set<string>(user.permissions),
    [user.permissions],
  );

  const can = useCallback(
    (permission: RbacPermission) => {
      if (!user.isAuthenticated) return false;
      return grants.has(permission);
    },
    [grants, user.isAuthenticated],
  );

  /**
   * Issue #87 fix — proper sign-out that actually ends the session.
   * Delegates to the pure `performSignOut` helper (unit-tested in
   * `auth-context.signOut.test.ts`) with React/router/query-cache
   * dependencies wired in. See the helper's doc for the full
   * teardown sequence.
   */
  const signOut = useCallback(async () => {
    await performSignOut({
      postLogout: (refreshToken) =>
        apiClient.post('/auth/logout', { refreshToken }).then(() => undefined),
      readToken: (key) =>
        typeof window !== 'undefined' ? localStorage.getItem(key) : null,
      clearTokens: () => {
        if (typeof window === 'undefined') return;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authContext');
      },
      clearQueryCache: () => queryClient.clear(),
      resetAuthState: () => {
        setUser(DEFAULT_USER);
        setContextKind('tenant');
      },
      navigateToLogin: () => navigate({ to: '/login', replace: true }),
    });
  }, [navigate, queryClient]);

  const value: AuthContextType = {
    user,
    isLoading,
    contextKind,
    isSysadmin: contextKind === 'sysadmin',
    can,
    refresh: fetchUser,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * #559 — Pre-fix this threw `Error('useAuth must be used within
 * AuthProvider')` whenever a consumer rendered briefly outside the
 * provider tree. Some downstream paths can read auth context during
 * portal/suspense fallback renders, fast-refresh swaps, or while the
 * AuthProvider tree is being torn down on sign-out. Throwing in those
 * windows surfaces as an unhandled red-console error on otherwise
 * healthy pages (sysadmin Home reported on Phase 2 build 981eac43).
 *
 * We now return a safe, non-authenticated fallback in that window —
 * the same shape the provider seeds with on initial mount before the
 * JWT decode resolves. The gate logic (`isLoading=true`,
 * `isSysadmin=false`, `user.isAuthenticated=false`) means every
 * consumer takes the "loading / not signed in" branch, which is the
 * correct behaviour for a component that genuinely is rendering
 * outside the provider — never crash, never silently flip a viewer
 * into an admin. A single dev-time warning preserves the original
 * developer signal without crashing production.
 */
const FALLBACK_AUTH_CONTEXT: AuthContextType = {
  user: DEFAULT_USER,
  isLoading: true,
  contextKind: 'tenant',
  isSysadmin: false,
  can: () => false,
  refresh: async () => {},
  signOut: async () => {},
};

let warnedOutsideProvider = false;

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    if (!warnedOutsideProvider) {
      warnedOutsideProvider = true;
      // Single dev-time warning so the original developer signal isn't
      // lost. Production builds suppress this via the env check; in
      // dev it points at the offending render so we can fix the tree.
      if (
        typeof import.meta !== 'undefined' &&
        (import.meta as { env?: { DEV?: boolean } }).env?.DEV
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          '[useAuth] called outside <AuthProvider>; returning unauthenticated fallback. ' +
            'Check that the component is rendered inside the provider tree.',
        );
      }
    }
    return FALLBACK_AUTH_CONTEXT;
  }
  return context;
}
